import { escapeHtml } from "@simplyk/common";
import * as QrCode from "qrcode";

const QR_CODE_WIDTH = 400;
const QR_CODE_MARGIN = 2;

async function generateQrDataUrl(url: string): Promise<string> {
  return QrCode.toDataURL(url, { width: QR_CODE_WIDTH, margin: QR_CODE_MARGIN });
}

export interface PrintableAuctionItem {
  title: string;
  formLink: string;
  startingBidLine: string;
  minimumBidIncrementLine: string | null;
}

export interface PrintAuctionQrCardsOptions {
  items: PrintableAuctionItem[];
  organizationName: string;
  organizationLogoUrl: string | null;
  hostedByLabel: string;
}

function buildCardHtml(item: PrintableAuctionItem, qrDataUrl: string, options: PrintAuctionQrCardsOptions): string {
  const logoHtml = options.organizationLogoUrl
    ? `<img src="${options.organizationLogoUrl}" alt="" style="width:64px;height:64px;object-fit:contain;border-radius:4px;" />`
    : "";

  const bidIncrementHtml = item.minimumBidIncrementLine
    ? `<p style="margin:0;font-family:'Roboto','Helvetica','Arial',sans-serif;font-size:13px;font-weight:600;line-height:19px;letter-spacing:0.12px;color:#0A093D;text-align:center;">${escapeHtml(item.minimumBidIncrementLine)}</p>`
    : "";

  return `
    <div style="page-break-after:always;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;gap:38px;padding:26px;box-sizing:border-box;">
      <div style="border:2px solid #C0C0D4;border-radius:13px;display:flex;flex-direction:column;align-items:center;padding:38px;gap:26px;">
        <img src="${qrDataUrl}" alt="QR Code" style="width:240px;height:240px;" />
        <div style="display:flex;flex-direction:column;align-items:center;gap:13px;width:100%;">
          <p style="margin:0;font-family:'Poppins','Helvetica','Arial',sans-serif;font-size:19px;font-weight:600;line-height:26px;color:#0A093D;text-align:center;">${escapeHtml(item.title)}</p>
          <div style="display:flex;flex-direction:column;align-items:center;gap:3px;">
            <p style="margin:0;font-family:'Roboto','Helvetica','Arial',sans-serif;font-size:13px;font-weight:600;line-height:19px;letter-spacing:0.12px;color:#0A093D;text-align:center;">${escapeHtml(item.startingBidLine)}</p>
            ${bidIncrementHtml}
          </div>
        </div>
      </div>
      <div style="display:flex;flex-direction:column;align-items:center;gap:10px;">
        ${logoHtml}
        <div style="display:flex;flex-direction:column;align-items:center;">
          <p style="margin:0;font-family:'Roboto','Helvetica','Arial',sans-serif;font-size:11px;font-weight:400;line-height:16px;letter-spacing:0.2px;color:#57568C;">${escapeHtml(options.hostedByLabel)}</p>
          <p style="margin:0;font-family:'Roboto','Helvetica','Arial',sans-serif;font-size:13px;font-weight:600;line-height:19px;letter-spacing:0.12px;color:#0A093D;">${escapeHtml(options.organizationName)}</p>
        </div>
      </div>
    </div>
  `;
}

function buildPrintDocument(cards: string[]): string {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@600&family=Roboto:wght@400;600&display=swap" rel="stylesheet" />
        <style>
          @page { margin: 0; }
          body { margin: 0; padding: 0; }
          @media print {
            html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>${cards.join("")}</body>
    </html>
  `;
}

export async function printAuctionQrCards(options: PrintAuctionQrCardsOptions): Promise<void> {
  const qrDataUrls = await Promise.all(options.items.map((item) => generateQrDataUrl(item.formLink)));

  const cards = options.items.map((item, index) => buildCardHtml(item, qrDataUrls[index], options));
  const html = buildPrintDocument(cards);

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.top = "-10000px";
  iframe.style.left = "-10000px";
  iframe.style.width = "0";
  iframe.style.height = "0";
  document.body.appendChild(iframe);

  const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDocument) {
    document.body.removeChild(iframe);
    return;
  }

  iframeDocument.open();
  iframeDocument.write(html);
  iframeDocument.close();

  await new Promise<void>((resolve) => {
    iframe.onload = () => resolve();
    setTimeout(resolve, 500);
  });

  iframe.contentWindow?.print();

  setTimeout(() => {
    document.body.removeChild(iframe);
  }, 1000);
}
